/* What is arriving, and what you are listening to, as one object.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PICTURE AND THE CONTROL ARE THE SAME THING
 *
 * The obvious build is a spectrum here and a row of filter buttons underneath.
 * That is two things to look at, and it leaves the most important question
 * unanswerable: when you pick a band, what did you stop hearing?
 *
 * So the band is drawn on the spectrum. The part you are listening to is at
 * full strength and the rest is dimmed -- still there, still moving, visibly
 * present and visibly not reaching your ears. Choosing "Fisken" lights
 * 100-580 Hz and that is exactly what changes in the headphones.
 *
 * This matters more here than it would on a music player. A filter that makes
 * the sea go quiet and a sea that has gone quiet sound identical, and one of
 * them is a fault report. Being able to see the signal continuing outside the
 * band is the difference.
 *
 * ---------------------------------------------------------------------------
 * NO Y-AXIS, AND THAT IS A RULE RATHER THAN A SIMPLIFICATION
 *
 * bands.ts:11-30 and the node's own segment contract say the same thing: until
 * a unit is bench-calibrated, every level is dBFS -- a number about the
 * recorder, not about the sea. So this draws shape and frequency, and no
 * amplitude scale at all. There is nothing here a person could read off as
 * "how loud", because there is nothing here that knows.
 *
 * ---------------------------------------------------------------------------
 * 20 Hz TO 10 kHz
 *
 * The A5's calibrated linear range. Above it the sensor rolls off and the live
 * stream's 64 kbit AAC cuts around 14 kHz, so the top third of a full-Nyquist
 * strip would be a picture of the encoder's psychoacoustic model. Drawing it
 * would invite someone to filter for something that is not there.
 *
 * Logarithmic, because an octave at the bottom of a fish's hearing is worth as
 * much as an octave at the top and a linear axis gives the first one four
 * pixels.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { CODEC_CEILING_HZ, TUNABLE, visibleBands } from '../bands'
import { DPR } from '../charts'
import { useT } from '../lang'

export interface Band { lo: number; hi: number }

const HEIGHT = 108
/* At rest there is no curve, so the height the curve would have needed is a
 * hole in the middle of the card. The strip collapses to its axis and its
 * hearing gutter -- still there, still saying what the frequencies mean, taking
 * the room a label takes rather than the room a chart takes -- and grows when
 * sound arrives. The jump is the picture waking up, which is the truth.
 *
 * Trimmed to the ink. Whatever sits above the resting line is dead space
 * inside the canvas box, and it reads as a gap between the label above and the
 * strip -- which put the label nearer the button than the strip and made three
 * evenly spaced things look like two and a straggler. */
const REST_HEIGHT = 32
/* Room under the curve for the hearing bracket, and only that. It used to
 * carry the frequency labels as well; they now flank the line. */
const GUTTER = 14

/* The frequency labels sit at the ENDS of the line, on its own baseline,
 * rather than under it -- so the strip reads as one object with a low and a
 * high end instead of a rule with captions beneath. That means the plot is
 * inset, and everything drawn in it has to be inset by the same amount or the
 * band would sit somewhere the axis does not agree with.
 *
 * Fixed rather than measured off the canvas, because the two labels never
 * change -- TUNABLE is 4 Hz to 16 kHz -- and the CSS that positions the drag
 * grips has to use the same numbers without being able to ask a canvas for
 * them. */
const AXIS_PAD_L = 34
const AXIS_PAD_R = 50

const LO = TUNABLE.lo
const HI = TUNABLE.hi

const logPos = (hz: number) =>
  (Math.log(Math.max(LO, Math.min(hz, HI))) - Math.log(LO))
  / (Math.log(HI) - Math.log(LO))

const posHz = (f: number) =>
  Math.exp(Math.log(LO) + f * (Math.log(HI) - Math.log(LO)))

const fmtHz = (hz: number) =>
  hz >= 1000 ? `${(hz / 1000).toFixed(hz % 1000 ? 1 : 0)} kHz` : `${Math.round(hz)} Hz`

/* The page's own face rather than the platform's. A canvas does not inherit
 * CSS, so an axis label left at `system-ui` was the one piece of type on the
 * page set in something else -- small, and enough to read as machine output. */
const UI_FONT = "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif"

/* Which handle a pointer is nearest, if either. Fourteen device-independent
 * pixels, the same reach LevelsChart uses for its brush edges -- wide enough
 * for a thumb, narrow enough that the two do not fight at a tight band. */
const GRAB_PX = 14

/** Where a grip sits, as CSS, matching `X()` in the paint loop exactly. */
const gripAt = (hz: number) =>
  `calc(${AXIS_PAD_L}px + (100% - ${AXIS_PAD_L + AXIS_PAD_R}px) * ${logPos(hz).toFixed(5)})`

export function Spectrum({
  analyser, band, onBand, editable, sampleRate, offsetDb,
}: {
  /** Null when nothing is playing, or when the graph could not be built. The
   *  strip still draws its axis and its band so the control does not vanish
   *  and reappear between presses. */
  analyser: AnalyserNode | null
  band: Band | null
  onBand: (b: Band) => void
  /** True only for the custom preset. The handles are not shown otherwise: a
   *  draggable edge on a preset invites a drag that would silently stop being
   *  the preset. */
  editable: boolean
  /* Only a fallback. The real figure is the AudioContext's own rate, which is
   * what the analyser's bins are laid out against -- a Mac running at 44.1 kHz
   * with 48000 passed in here would place every frequency 9% wrong, and the
   * fish band would be drawn over the wrong part of the picture. */
  sampleRate: number
  /* `SPL = dBFS + offsetDb`, summed by the server and null unless every
   * calibration term is present. Null is the ordinary state and the reason
   * this file has spent its life with no y-axis at all: until a node is
   * bench-calibrated every level is dBFS, which is a number about the recorder
   * rather than about the sea.
   *
   * When it is not null the axis is drawn, and it is drawn on the analyser's
   * own dBFS -- which is legitimate because the analyser sits BEFORE the
   * normalisation gain and before the filters in both halves of the player, so
   * what it reports is the level in the file rather than the level we made
   * comfortable to listen to. That ordering is load-bearing; moving the
   * analyser after the gain would make this axis wrong by however much the
   * quiet-sea lift happened to be. */
  offsetDb?: number | null
}) {
  const t = useT()
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const wrap = useRef<HTMLDivElement | null>(null)
  const bandRef = useRef(band)
  bandRef.current = band
  const [drag, setDrag] = useState<'lo' | 'hi' | null>(null)

  /* The paint loop.
   *
   * One requestAnimationFrame for the life of the component rather than one
   * per render: the analyser is polled, not subscribed to, and re-entering
   * through React sixty times a second would re-run every hook on the page to
   * move a few hundred pixels.
   */
  const offRef = useRef(offsetDb)
  offRef.current = offsetDb

  useEffect(() => {
    const el = canvas.current
    if (!el) return
    let raf = 0
    let bins: Float32Array | null = null
    /* A slow floor and ceiling, so the picture uses its whole height without
     * jumping. Recomputed per frame from the frame itself would make a quiet
     * sea look identical to a loud one -- which is the one comparison the
     * strip is for. These ease towards the observed range instead, fast enough
     * to follow a passing vessel and slow enough not to breathe. */
    let floor = -110
    let ceil = -30

    const paint = () => {
      raf = requestAnimationFrame(paint)
      const dpr = DPR()
      const rect = el.getBoundingClientRect()
      const w = Math.max(64, Math.round(rect.width))
      const h = analyser ? HEIGHT : REST_HEIGHT
      if (el.width !== Math.round(w * dpr) || el.height !== Math.round(h * dpr)) {
        el.width = Math.round(w * dpr)
        el.height = Math.round(h * dpr)
      }
      const c = el.getContext('2d')
      if (!c) return
      c.setTransform(dpr, 0, 0, dpr, 0, 0)
      c.clearRect(0, 0, w, h)

      const plot = h - GUTTER
      // The drawable span, between the two frequency labels.
      const left = AXIS_PAD_L
      const right = Math.max(left + 8, w - AXIS_PAD_R)
      const span = right - left
      /** Hz -> pixels, inset. Everything in this component goes through it. */
      const X = (hz: number) => left + logPos(hz) * span
      const b = bandRef.current
      const x0 = b ? X(b.lo) : left
      const x1 = b ? X(b.hi) : right

      /* The band, as a lit region behind the curve. Drawn first so the curve
       * sits on top of it rather than being tinted by it.
       *
       * Graded from the top down rather than a flat wash. A rectangle of even
       * tint is a rectangle; the same tint fading out has a light source, and
       * costs one gradient. It also puts the strongest part of the fill where
       * the curve's peaks are, so the band reads as lit rather than as a
       * coloured-in region of a chart. */
      /* THE BAND GETS NO REGION OF ITS OWN, AND THAT IS THE WHOLE FIX.
       *
       * There was a tinted rectangle here, full plot height, behind the curve.
       * It was reported as reading like the selected frequencies had been
       * REMOVED -- the opposite of what it meant -- and once seen it cannot be
       * unseen: a lighter slab from the top of the plot to the bottom, with the
       * curve faintly visible inside it, is a panel laid OVER that part of the
       * picture. Cover something and you have hidden it, whatever the caption
       * says.
       *
       * The mistake was having two mechanisms for one message. The curve is
       * already filled inside the band and reduced to a hairline outside it,
       * which says "this part reaches your ears" using the data itself. The
       * rectangle said it again, louder, in a way that contradicted it.
       *
       * So: one statement. Filled curve inside, hairline outside, and thin
       * edges to mark where the band begins and ends. Nothing is drawn above
       * the curve, so nothing can read as laid on top of it. */

      /* At rest.
       *
       * Nothing is playing, so there is no curve -- and an empty box with two
       * short rules floating in it read as a chart that had failed rather than
       * one that was waiting. A floor drawn all the way across says the picture
       * is intact and the sea has not arrived yet, which is the true reading. */
      if (!analyser) {
        /* Two strokes rather than one, and both graded.
         *
         * The floor runs the full width and fades at the ends, so the line
         * belongs to the card instead of stopping dead against its padding.
         * The band is redrawn over it at twice the weight and full strength,
         * which is the whole message of this component at rest: this part is
         * what you would hear. At 1px flat the two were nearly the same line
         * and the strip read as a divider. */
        const across = c.createLinearGradient(left, 0, right, 0)
        across.addColorStop(0, 'rgba(147, 171, 159, 0.08)')
        across.addColorStop(0.5, 'rgba(147, 171, 159, 0.46)')
        across.addColorStop(1, 'rgba(147, 171, 159, 0.08)')
        c.strokeStyle = across
        c.lineWidth = 3
        c.lineCap = 'round'
        c.beginPath()
        c.moveTo(left + 1.5, plot - 1.5)
        c.lineTo(right - 1.5, plot - 1.5)
        c.stroke()
        c.lineCap = 'butt'

        if (b) {
          const inBand = c.createLinearGradient(x0, 0, x1, 0)
          inBand.addColorStop(0, 'rgba(180, 217, 199, 0.30)')
          inBand.addColorStop(0.5, 'rgba(180, 217, 199, 0.92)')
          inBand.addColorStop(1, 'rgba(180, 217, 199, 0.30)')
          c.strokeStyle = inBand
          c.lineWidth = 4
          c.lineCap = 'round'
          c.beginPath()
          c.moveTo(x0 + 2, plot - 1.5)
          c.lineTo(x1 - 2, plot - 1.5)
          c.stroke()
          c.lineCap = 'butt'
        }
      }

      if (analyser) {
        const rate = analyser.context.sampleRate || sampleRate
        if (!bins || bins.length !== analyser.frequencyBinCount) {
          bins = new Float32Array(analyser.frequencyBinCount)
        }
        analyser.getFloatFrequencyData(bins)
        const perBin = (rate / 2) / bins.length

        // The observed range, eased. minDecibels is -110 on the live analyser
        // and a bin sitting exactly on it carries no information, so it is
        // excluded from the ceiling rather than dragging it down.
        let seen = -Infinity
        for (let i = 1; i < bins.length; i++) {
          const v = bins[i]!
          if (v > seen && Number.isFinite(v)) seen = v
        }
        if (Number.isFinite(seen)) {
          ceil += (Math.max(seen + 4, floor + 20) - ceil) * 0.05
        }
        floor += ((ceil - 70) - floor) * 0.05

        /* One column per pixel, taking the loudest bin that falls in it.
         *
         * Maximum rather than mean, which is the same choice levels.series
         * makes on the server and for the same reason: a three-second
         * transient averaged into a wide column disappears completely, and a
         * transient is the thing worth hearing. Below about 400 Hz a pixel is
         * narrower than a bin, so it reads the nearest one and the curve is
         * interpolated rather than aliased. */
        /* Held as a Path2D rather than as the context's current path, and
         * that is a bug fix rather than a tidy-up.
         *
         * `clip()` does not reset the current path. So `beginPath(); rect();
         * clip();` leaves the RECTANGLE as the current path, and the `fill()`
         * that followed filled the clip rectangle -- a solid block from the
         * top of the plot to the bottom, across the whole band -- instead of
         * the area under the curve. That is why selecting a filter looked like
         * the chosen frequencies had been covered over: they had been. It shipped
         * in the commit that introduced the inside/outside split and survived a
         * screenshot review, because with a loud band the slab's lower edge sat
         * near the curve and the difference was only the flat top.
         *
         * A Path2D cannot be clobbered by anything the context does to its own
         * path, so `fill(curve)` fills the curve whatever has happened since. */
        const curve = new Path2D()
        curve.moveTo(left, plot)
        for (let x = left; x <= right; x++) {
          const hzLo = posHz((x - left) / span)
          const hzHi = posHz(Math.min(1, (x + 1 - left) / span))
          const i0 = Math.max(1, Math.floor(hzLo / perBin))
          const i1 = Math.max(i0, Math.min(bins.length - 1, Math.ceil(hzHi / perBin)))
          let peak = -Infinity
          for (let i = i0; i <= i1; i++) if (bins[i]! > peak) peak = bins[i]!
          const norm = (peak - floor) / Math.max(1, ceil - floor)
          const y = plot - Math.max(0, Math.min(1, norm)) * (plot - 2)
          curve.lineTo(x, y)
        }
        curve.lineTo(right, plot)
        curve.closePath()

        /* FILLED IS WHAT YOU HEAR. OUTLINED IS WHAT THE SENSOR STILL HEARS.
         *
         * Both passes draw the same shape, so the curve cannot disagree with
         * itself at the boundary -- but only the band is filled. Outside it
         * the curve drops to a hairline.
         *
         * The obvious move is to mask the outside away entirely, so the
         * picture is exactly the sound reaching the ear. It is the wrong move
         * here, and the reason is at the top of this file: a filter that makes
         * the sea go quiet and a sea that has gone quiet sound identical, and
         * one of them is a fault report. Pick `Fisken`, hear nothing, and with
         * the rest of the spectrum hidden there is no way to tell whether
         * 100-580 Hz is quiet or the hydrophone is dead.
         *
         * A 35% fill was too much of an answer to that -- two solid regions
         * competing, so the picture did not read as "this is what you hear". A
         * line is enough: it cannot be mistaken for the signal you are
         * listening to, and it still moves, which is the whole job. What you
         * hear has area; what you do not has only a pulse. */
        if (b) {
          /* A FAINT FILL EVERYWHERE FIRST, so the picture has no hole in it.
           *
           * With the area under the curve filled inside the band and empty
           * outside it, the band became the only lit region on a dark strip
           * with a hard edge either side -- and a lighter rectangle with hard
           * edges is a panel laid over a picture, whatever it is supposed to
           * mean. It was read as the selected frequencies having been removed,
           * which is the exact opposite of the truth.
           *
           * Filled faintly throughout and brightly inside the band, the same
           * information reads as a light being shone on part of the spectrum.
           * Nothing is missing anywhere, so nothing can look taken away.
           *
           * 0.13, not the 0.35 this started at. That was two solid regions
           * competing and the band stopped being the subject; this is a floor
           * the eye reads as continuation. */
          c.fillStyle = 'rgba(180, 217, 199, 0.13)'
          c.fill(curve)
          c.strokeStyle = 'rgba(147, 171, 159, 0.55)'
          c.lineWidth = 1
          c.stroke(curve)
          c.save()
          c.beginPath()
          c.rect(x0, 0, Math.max(1, x1 - x0), plot)
          c.clip()
          c.fillStyle = 'rgba(180, 217, 199, 0.85)'
          c.fill(curve)
          c.restore()
        } else {
          // Nothing is filtered, so everything is what you hear.
          c.fillStyle = 'rgba(180, 217, 199, 0.85)'
          c.fill(curve)
        }
      }

      /* The band edges. Thin, and only where a band is set -- a line drawn
       * across an unfiltered spectrum would be a boundary that means nothing.
       *
       * Full height ONLY while the band is draggable, because there the edge
       * is a handle and has to be grabbable along its length. Everywhere else
       * they are short ticks rising off the axis: a vertical rule at each end
       * of a filled region closes it into a rectangle, and a rectangle is the
       * thing that read as a panel covering the spectrum rather than as part
       * of it. The fill already says where the band is; the ticks only need to
       * say exactly where it ends. */
      if (b) {
        c.strokeStyle = 'rgba(180, 217, 199, 0.9)'
        c.lineWidth = 1
        const top = editable ? 0 : plot - (analyser ? 12 : 6)
        for (const x of [x0, x1]) {
          c.beginPath()
          c.moveTo(Math.round(x) + 0.5, top)
          c.lineTo(Math.round(x) + 0.5, plot)
          c.stroke()
        }
      }

      /* Where the stream stops carrying anything.
       *
       * Live and the archive proxy are both 64 kbit AAC, which gives up around
       * 14 kHz. Only drawn when a band reaches past it, because the rest of
       * the time it is a line about the encoder on a picture about the sea --
       * but when the feeder preset is selected, the top of that band is
       * genuinely not in these bytes and the picture should say so rather than
       * showing an empty region the listener reads as silence. */
      if (b && b.hi > CODEC_CEILING_HZ) {
        const cx = Math.round(X(CODEC_CEILING_HZ)) + 0.5
        c.save()
        c.setLineDash([2, 3])
        c.strokeStyle = 'rgba(147, 171, 159, 0.7)'
        c.lineWidth = 1
        c.beginPath()
        c.moveTo(cx, 0)
        c.lineTo(cx, plot)
        c.stroke()
        c.restore()
      }

      /* The level axis, when there is one to draw.
       *
       * Two lines and two labels, at round decibels inside whatever range the
       * picture is currently using. Not a full grid: the strip is 108px tall
       * and the question it answers is "how loud is that, roughly", not "read
       * me a value off a chart". A denser axis would imply a precision that
       * +/-4 dB of datasheet sensitivity does not support.
       *
       * Deliberately absent when `offsetDb` is null, rather than shown in
       * dBFS. A dBFS scale is a number about this recorder that a reader would
       * take for a number about the sea, and rule 5 exists because those look
       * identical on a chart. */
      const off = offRef.current
      if (analyser && off != null && Number.isFinite(off)) {
        const lo = floor + off
        const hi = ceil + off
        // A step that puts two or three lines in view at any zoom. The eased
        // range moves, so the step is chosen from it rather than fixed.
        const span = Math.max(1, hi - lo)
        const step = span > 60 ? 20 : span > 30 ? 10 : 5
        c.font = '11px ' + UI_FONT
        c.textAlign = 'left'
        c.textBaseline = 'bottom'
        for (let v = Math.ceil(lo / step) * step; v < hi; v += step) {
          const y = plot - ((v - lo) / span) * (plot - 2)
          if (y < 10 || y > plot - 2) continue
          c.strokeStyle = 'rgba(147, 171, 159, 0.22)'
          c.lineWidth = 1
          c.beginPath()
          c.moveTo(left, Math.round(y) + 0.5)
          c.lineTo(right, Math.round(y) + 0.5)
          c.stroke()
          c.fillStyle = '#b9cec3'
          c.fillText(String(Math.round(v)), left + 3, Math.round(y) - 2)
        }
      }

      /* The gutter: where the fish are, on the same axis.
       *
       * This is the translation layer bands.ts was written to be -- a
       * spectrogram tells an acoustician a great deal and a farmer nothing,
       * and the useful sentence is "this part is the part they can hear". */
      c.font = '12px ' + UI_FONT
      c.textBaseline = 'top'
      for (const zone of visibleBands(analyser?.context.sampleRate ?? sampleRate)) {
        if (zone.kind !== 'hearing' || zone.lo > HI) continue
        const zx0 = X(zone.lo)
        const zx1 = X(Math.min(zone.hi, HI))
        c.strokeStyle = 'rgba(147, 171, 159, 0.34)'
        c.lineWidth = 1
        // Bracketed rather than a bare rule. Two floating line segments under
        // an empty chart looked like breakage; a bracket looks like a span,
        // which is what it is.
        c.beginPath()
        c.moveTo(zx0 + 0.5, plot + 3)
        c.lineTo(zx0 + 0.5, plot + 8)
        c.moveTo(zx0, plot + 5.5)
        c.lineTo(zx1, plot + 5.5)
        c.moveTo(zx1 - 0.5, plot + 3)
        c.lineTo(zx1 - 0.5, plot + 8)
        c.stroke()
      }
      /* Flanking the line rather than hanging under it. Under it they read as
       * two captions belonging to nothing in particular; at the ends, on the
       * line's own baseline, they are what the line runs between. */
      c.fillStyle = '#93ab9f'
      c.textBaseline = 'middle'
      c.textAlign = 'left'
      c.fillText(fmtHz(LO), 0, plot - 1)
      c.textAlign = 'right'
      c.fillText(fmtHz(HI), w, plot - 1)
      c.textBaseline = 'top'
    }

    raf = requestAnimationFrame(paint)
    return () => cancelAnimationFrame(raf)
  }, [analyser, sampleRate])

  /* Dragging an edge.
   *
   * Pointer events rather than mouse or touch, so a finger, a pen and a mouse
   * are one code path. The handles never cross: the low edge stops an octave
   * short of the high one, because a band of zero width is silence and
   * silence is the one thing a listener cannot debug.
   */
  /* Pixels within the element -> Hz, through the SAME inset the canvas uses.
   * Miss this and the grips land where the axis is not: a drag would set a
   * frequency the picture disagrees with, by about forty pixels' worth. */
  const frac = (px: number, width: number) => {
    const s = Math.max(8, width - AXIS_PAD_L - AXIS_PAD_R)
    return Math.max(0, Math.min(1, (px - AXIS_PAD_L) / s))
  }

  const at = useCallback((e: React.PointerEvent) => {
    const box = wrap.current?.getBoundingClientRect()
    if (!box) return LO
    return posHz(frac(e.clientX - box.left, box.width))
  }, [])

  const down = (e: React.PointerEvent) => {
    if (!editable || !band) return
    const box = wrap.current?.getBoundingClientRect()
    if (!box) return
    const x = e.clientX - box.left
    const px = (hz: number) =>
      AXIS_PAD_L + logPos(hz) * Math.max(8, box.width - AXIS_PAD_L - AXIS_PAD_R)
    const dLo = Math.abs(x - px(band.lo))
    const dHi = Math.abs(x - px(band.hi))
    const near = Math.min(dLo, dHi)
    if (near > GRAB_PX) return
    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag(dLo <= dHi ? 'lo' : 'hi')
  }

  const move = (e: React.PointerEvent) => {
    if (!drag || !band) return
    const hz = at(e)
    if (drag === 'lo') onBand({ lo: Math.min(hz, band.hi / 1.2), hi: band.hi })
    else onBand({ lo: band.lo, hi: Math.max(hz, band.lo * 1.2) })
  }

  const up = (e: React.PointerEvent) => {
    if (!drag) return
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* gone */ }
    setDrag(null)
  }

  /* Keyboard, because a band is a value and values are editable without a
   * mouse. Arrows move the nearest edge by a twelfth of an octave -- a
   * semitone, which is a step a person can hear. */
  const key = (e: React.KeyboardEvent) => {
    if (!editable || !band) return
    const step = 2 ** (1 / 12)
    if (e.key === 'ArrowLeft') onBand({ lo: Math.max(LO, band.lo / step), hi: band.hi })
    else if (e.key === 'ArrowRight') onBand({ lo: Math.min(band.hi / 1.2, band.lo * step), hi: band.hi })
    else if (e.key === 'ArrowUp') onBand({ lo: band.lo, hi: Math.min(HI, band.hi * step) })
    else if (e.key === 'ArrowDown') onBand({ lo: band.lo, hi: Math.max(band.lo * 1.2, band.hi / step) })
    else return
    e.preventDefault()
  }

  return (
    <div
      ref={wrap}
      className={`spectrum${editable ? ' tunable' : ''}${drag ? ' dragging' : ''}`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onKeyDown={key}
      tabIndex={editable ? 0 : -1}
      role={editable ? 'group' : 'img'}
      aria-label={band
        ? t('Sound from {lo} to {hi}')
          .replace('{lo}', fmtHz(band.lo)).replace('{hi}', fmtHz(band.hi))
        : t('All the sound the hydrophone hears')}
    >
      <canvas
        ref={canvas}
        style={{ width: '100%', height: analyser ? HEIGHT : REST_HEIGHT }}
      />
      {editable && band && (
        <>
          {/* The same inset, in CSS. `calc` rather than a percentage, since
              the span is the element minus two fixed pads. */}
          <i className="spectrum-grip" style={{ left: gripAt(band.lo) }} />
          <i className="spectrum-grip" style={{ left: gripAt(band.hi) }} />
        </>
      )}
    </div>
  )
}

export { fmtHz }
